import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Tag, Plus, X } from "lucide-react";

interface Label {
  id: number;
  name: string;
  createdAt: string;
}

interface EntityLabelsProps {
  entityType: "advertiser" | "affiliate" | "offer";
  entityId: string;
  apiBase: string;
}

export function EntityLabels({ entityType, entityId, apiBase }: EntityLabelsProps) {
  const queryClient = useQueryClient();
  const [showPicker, setShowPicker] = useState(false);

  const { data: assignedData, isLoading: loadingAssigned } = useQuery({
    queryKey: ["entity-labels", entityType, entityId],
    queryFn: async () => {
      const res = await api.get(`${apiBase}/${entityId}/labels`);
      if (!res.ok) throw new Error("Failed to fetch labels");
      return res.json() as Promise<{ data: Label[] }>;
    },
  });

  const { data: allLabelsData } = useQuery({
    queryKey: ["labels"],
    queryFn: async () => {
      const res = await api.get("/v1/labels");
      if (!res.ok) throw new Error("Failed to fetch labels");
      return res.json() as Promise<{ data: Label[] }>;
    },
    enabled: showPicker,
  });

  const addMutation = useMutation({
    mutationFn: (labelIds: number[]) =>
      api.post(`${apiBase}/${entityId}/labels`, { labelIds }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["entity-labels", entityType, entityId] });
    },
  });

  const removeMutation = useMutation({
    mutationFn: (labelId: number) =>
      api.delete(`${apiBase}/${entityId}/labels/${labelId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["entity-labels", entityType, entityId] });
    },
  });

  const assigned = assignedData?.data || [];
  const allLabels = allLabelsData?.data || [];
  const assignedIds = new Set(assigned.map((l: Label) => l.id));
  const available = allLabels.filter((l: Label) => !assignedIds.has(l.id));

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-medium text-gray-900">Labels</h3>
        <button
          onClick={() => setShowPicker(!showPicker)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          Add Label
        </button>
      </div>

      {showPicker && (
        <div className="mb-4 bg-white border border-gray-200 rounded-xl p-3">
          {available.length === 0 ? (
            <p className="text-xs text-gray-500">
              {allLabels.length === 0
                ? "No labels exist yet. Create them in Settings > Labels."
                : "All labels are already assigned."}
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {available.map((label: Label) => (
                <button
                  key={label.id}
                  onClick={() => addMutation.mutate([label.id])}
                  disabled={addMutation.isPending}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-gray-50 text-gray-700 border border-gray-200 rounded-full hover:bg-gray-100 hover:border-gray-300 transition-colors"
                >
                  <Plus className="w-3 h-3" />
                  {label.name}
                </button>
              ))}
            </div>
          )}
          <button
            onClick={() => setShowPicker(false)}
            className="text-xs text-gray-500 hover:text-gray-700 mt-2"
          >
            Close
          </button>
        </div>
      )}

      {loadingAssigned ? (
        <div className="animate-pulse h-8 bg-gray-100 rounded-lg w-48" />
      ) : assigned.length === 0 ? (
        <div className="text-center py-8 border border-dashed border-gray-300 rounded-xl">
          <Tag className="w-6 h-6 text-gray-300 mx-auto mb-2" />
          <p className="text-xs text-gray-500">No labels assigned</p>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {assigned.map((label: Label) => (
            <span
              key={label.id}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 rounded-full"
            >
              <Tag className="w-3 h-3" />
              {label.name}
              <button
                onClick={() => removeMutation.mutate(label.id)}
                disabled={removeMutation.isPending}
                className="text-blue-400 hover:text-blue-600 transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
